/**
 * CRM per-user Calendar integration API slice (Calendar Inc0 UI).
 *
 * Per-user, instance-scoped two-way calendar grant. The panel renders PURELY from
 * GET /crm/calendar/status (no tokens ever leave the server). Connect = fetch the
 * provider's OAuth authorize URL and redirect the browser to it; the IdP callback
 * lands back on /crm/profile?integration=calendar&status=connected|error. Disconnect
 * revokes the active grant.
 *
 * Route shapes mirror the as-built Inc0 backend (server/routes/crmCalendar.ts):
 *   - auth-url provider is a PATH param: /crm/calendar/:provider/auth-url
 *   - disconnect is DELETE /crm/calendar  (not /disconnect)
 *
 * This increment consumes only the REACHABLE states (A not-connected, B connecting,
 * C active). 'expired' / last_error states are produced only by Inc1's refresh worker
 * and are deferred there.
 */
import { baseApi } from "./baseApi";

export type CalendarProviderKey = "microsoft" | "google";

/** Safe status from GET /crm/calendar/status — NEVER carries tokens. */
export interface CalendarStatus {
  connected: boolean;
  provider: CalendarProviderKey | null;
  providerAccountEmail: string | null;
  /** 'active' is the only value reachable today; 'expired' arrives with Inc1. */
  status: "active" | "expired" | "revoked" | null;
  lastError: string | null;
  lastSyncedAt: string | null;
  tokenExpiresAt: string | null;
}

/** Per-user sync DIRECTION (1-way / 2-way), modelled as two booleans server-side.
 *  1-way = {outbound:true, inbound:false}; 2-way = {outbound:true, inbound:true}. */
export interface CalendarPrefs {
  syncOutbound: boolean;
  syncInbound: boolean;
}

/** Outcome of a manual "Sync now". */
export type CalendarSyncNowResult = "synced" | "in_progress" | "cooldown" | "not_connected";

export const crmCalendarApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCalendarStatus: builder.query<CalendarStatus, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/crm/calendar/status", params: { instanceId } }),
      providesTags: ["CrmCalendar"],
    }),
    // The caller's direction prefs. A user with no row resolves to both-true (two-way).
    getCalendarPrefs: builder.query<CalendarPrefs, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/crm/calendar/prefs", params: { instanceId } }),
      providesTags: ["CrmCalendarPrefs"],
    }),
    updateCalendarPrefs: builder.mutation<
      CalendarPrefs,
      { instanceId: string; syncOutbound: boolean; syncInbound: boolean }
    >({
      query: ({ instanceId, syncOutbound, syncInbound }) => ({
        url: "/crm/calendar/prefs",
        method: "PUT",
        params: { instanceId },
        body: { syncOutbound, syncInbound },
      }),
      invalidatesTags: ["CrmCalendarPrefs"],
    }),
    // Manual on-demand sync. Result: synced | in_progress | cooldown | not_connected.
    syncCalendarNow: builder.mutation<{ status: CalendarSyncNowResult }, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/crm/calendar/sync",
        method: "POST",
        params: { instanceId },
      }),
      // A successful run advances last-synced + mirror; refresh the status card.
      invalidatesTags: ["CrmCalendar"],
    }),
    // Lazy: triggered on the Connect click, then the browser is redirected to authUrl.
    getCalendarAuthUrl: builder.query<
      { authUrl: string },
      { instanceId: string; provider: CalendarProviderKey; returnPath?: string }
    >({
      query: ({ instanceId, provider, returnPath }) => ({
        url: `/crm/calendar/${provider}/auth-url`,
        params: { instanceId, ...(returnPath ? { returnPath } : {}) },
      }),
    }),
    // Lazy: admin-consent URL for orgs that require an administrator to approve the app.
    getCalendarAdminConsentUrl: builder.query<
      { adminConsentUrl: string },
      { instanceId: string; provider: CalendarProviderKey; returnPath?: string }
    >({
      query: ({ instanceId, provider, returnPath }) => ({
        url: `/crm/calendar/${provider}/admin-consent-url`,
        params: { instanceId, ...(returnPath ? { returnPath } : {}) },
      }),
    }),
    disconnectCalendar: builder.mutation<
      { success: boolean; revoked: boolean },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({
        url: "/crm/calendar",
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmCalendar"],
    }),
  }),
});

export const {
  useGetCalendarStatusQuery,
  useGetCalendarPrefsQuery,
  useUpdateCalendarPrefsMutation,
  useSyncCalendarNowMutation,
  useLazyGetCalendarAuthUrlQuery,
  useLazyGetCalendarAdminConsentUrlQuery,
  useDisconnectCalendarMutation,
} = crmCalendarApi;
